"use client";

import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';

/**
 * Hook that guards entire pages — redirects to signin if not logged in.
 * Use for protected pages like /profile, /settings.
 */
export function useRequireAuth(redirectUrl: string = '/signin') {
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !user) {
      // Save the page the user was trying to visit so we can restore it after sign-in.
      if (typeof window !== 'undefined') {
        const returnTo = window.location.pathname + window.location.search;
        // Don't save /signin itself as the return destination
        if (returnTo && returnTo !== '/signin') {
          sessionStorage.setItem('auth_return_to', returnTo);
        }
      }
      router.push(redirectUrl);
    }
  }, [user, loading, router, redirectUrl]);

  return { user, loading };
}

/**
 * Hook that provides a guard function for protected actions.
 * If user is not logged in, redirects to signin page when action is attempted.
 * Does NOT auto-redirect on mount — suitable for public pages with some protected actions.
 *
 * Usage:
 *   const { user, guardAction } = useAuthGuard();
 *   <Button onClick={guardAction(() => handleFollow())}>Follow</Button>
 */
export function useAuthGuard() {
  const { user, userProfile, loading } = useAuth();
  const router = useRouter();

  const guardAction = useCallback(
    (fn: (...args: any[]) => any) => {
      return (...args: any[]) => {
        if (!user) {
          router.push('/signin');
          return;
        }
        return fn(...args);
      };
    },
    [user, router]
  );

  return { user, userProfile, loading, guardAction };
}
